import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useReducedMotion, useMotionValue, animate, MotionValue } from "framer-motion";
import NetworkToFoundationDiagram from "./NetworkToFoundationDiagram";

const ease = [0.22, 1, 0.36, 1] as const;

const ChaosToOrder = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const mobileDiagramRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion() ?? false;

  // Desktop scroll-driven progress.
  const { scrollYProgress: desktopProgress } = useScroll({
    target: sectionRef,
    offset: ["start 60%", "end end"],
  });

  // Reduced-motion fallback: render the diagram in its final state.
  const finalProgress = useMotionValue(1);
  // Mobile: animate once on enter view.
  const mobileAnimatedProgress = useMotionValue(0);


  // Track lg breakpoint (lazy init prevents SSR/first-paint mount of the wrong branch).
  const [isLg, setIsLg] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 1024px)").matches
      : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsLg(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Mobile: play diagram animation once on first intersection.
  useEffect(() => {
    if (isLg || reduce) return;
    const el = mobileDiagramRef.current;
    if (!el) return;
    let hasPlayed = false;
    let controls: ReturnType<typeof animate> | null = null;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasPlayed) return;
        hasPlayed = true;
        mobileAnimatedProgress.set(0);
        controls = animate(mobileAnimatedProgress, 1, {
          duration: 5.5,
          ease: [0.55, 0, 0.45, 1],
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      controls?.stop();
    };
  }, [isLg, reduce, mobileAnimatedProgress]);

  // Select after all hooks have run (no conditional hook calls).
  const progress: MotionValue<number> = reduce
    ? finalProgress
    : isLg
    ? desktopProgress
    : mobileAnimatedProgress;

  const problemContent = (
    <>
      <p className="eyebrow text-muted-foreground">The problem</p>
      <h2 className="display mt-3 lg:mt-5 text-2xl md:text-3xl lg:text-section-title text-foreground">
        AI adoption has <span className="italic font-normal text-primary/95">outpaced</span> infrastructure.
      </h2>
      <p className="mt-4 lg:mt-7 text-sm md:text-base lg:text-lg text-foreground/70 leading-relaxed max-w-md">
        Health systems are managing more AI vendors, workflows, and departments
        than their integration and governance models were built for. The result
        is fragmentation, risk, and stalled value.
      </p>
    </>
  );

  const shiftContent = (
    <>
      <p className="eyebrow text-muted-foreground">The shift</p>
      <h2 className="display mt-3 lg:mt-5 text-2xl md:text-3xl lg:text-section-title text-foreground">
        From fragmented AI to <span className="italic font-normal text-primary/95">one</span> operating layer.
      </h2>
      <p className="mt-4 lg:mt-7 text-sm md:text-base lg:text-lg text-foreground/70 leading-relaxed max-w-md">
        deepc gives AI workflows one integration foundation, one governance model, and one evidence trail.
      </p>
    </>
  );

  const governedContent = (
    <>
      <p className="eyebrow text-muted-foreground">Governed extensibility</p>
      <h2 className="display mt-3 lg:mt-5 text-2xl md:text-3xl lg:text-section-title text-foreground">
        Any AI capability. Any workflow.<br className="hidden lg:inline" />{" "}
        <span className="italic font-normal text-primary/95">One</span> foundation.
      </h2>
      <div className="mt-5 lg:mt-7 flex flex-wrap gap-2.5">
        {["Same integration layer", "Same governance model", "Same evidence trail"].map((c) => (
          <span
            key={c}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3.5 py-1.5 text-xs text-foreground/80"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
            {c}
          </span>
        ))}
      </div>
      <div className="mt-6 lg:mt-8">
        <Link
          to="/solution"
          className="group inline-flex items-center gap-2 text-foreground font-medium border-b border-foreground/30 pb-1 hover:border-primary transition-colors duration-300"
        >
          Explore the solution
          <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </Link>
      </div>
    </>
  );

  const cardMotion = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.4 },
    transition: { duration: 0.8, ease },
  };

  return (
    <section
      ref={sectionRef}
      className="relative bg-background text-foreground"
      style={{ minHeight: reduce || !isLg ? "auto" : "140vh" }}
    >
      {/* Faint dotted-grid background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(hsl(220 30% 25%) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        aria-hidden
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 50%, hsl(var(--primary) / 0.10), transparent 70%)",
        }}
      />

      <div className="container relative z-10 py-section-tight">
        {isLg ? (
          /* Desktop: two-column with sticky diagram */
          <div className="grid grid-cols-12 gap-6 xl:gap-8">
            <div className="col-span-4 space-y-[60vh] pt-[10vh]">
              <motion.div {...cardMotion}>{problemContent}</motion.div>
              <motion.div {...cardMotion}>{shiftContent}</motion.div>
              <motion.div {...cardMotion} className="pb-40">
                {governedContent}
              </motion.div>
            </div>

            <div className="col-span-8">
              <div className="sticky top-[80px] h-[calc(100vh-80px)] flex items-center justify-center">
                <div className="w-full">
                  <NetworkToFoundationDiagram progress={progress} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Mobile / tablet: simple vertical sequence */
          <div className="space-y-14">
            <motion.div {...cardMotion}>{problemContent}</motion.div>

            <div ref={mobileDiagramRef} className="my-10 w-full">
              <NetworkToFoundationDiagram progress={progress} />
            </div>

            <motion.div
              {...cardMotion}
              className="pt-10 border-t border-border/40"
            >
              {shiftContent}
            </motion.div>

            <motion.div
              {...cardMotion}
              className="pt-10 border-t border-border/40"
            >
              {governedContent}
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ChaosToOrder;
